import { useEffect, useRef, useState } from 'react'
import { createSocketConnection, socketLog, UPDATE_THROTTLE_MS } from './socketClient'
import config from '../config/config'

export const DEVICE_DASHBOARD_ID = 'nsyncBmsDashboardSocket'
const DEVICE_UPDATE_EVENT = 'deviceUpdate'
const NSYNC_UPDATE_EVENT = 'nsyncUpdate'

// Returns null when there's no auth token (see createSocketConnection).
export const connectDeviceDashboardSocket = (assetId, { bmsFilter } = {}) => {
  const socket = createSocketConnection('device-dashboard')
  if (!socket) return null

  socket.on('connect', () => {
    const payload = { assetId, dashboardId: DEVICE_DASHBOARD_ID, ...(bmsFilter !== undefined && bmsFilter !== null ? { bmsFilter } : {}) }
    socketLog('[socket:device-dashboard] subscribe ->', payload)
    socket.emit('subscribe', payload)
  })

  // Never console.log stream payloads here: with DevTools open the console
  // retains every logged object forever, leaking the full telemetry stream.
  return socket
}

/**
 * Subscribes to the whole-VIM dashboard (no bmsFilter). Streams both
 * `deviceUpdate` (VIM-level info — see mapDeviceUpdateToVimData) and
 * `nsyncUpdate` (the full unfiltered list of active BMS nodes — see
 * mapNsyncUpdateToActiveNodes).
 *
 * `enabled` (default true) tears the socket down entirely when false — used
 * by BmsDashboard to disconnect while viewing a historical range instead of
 * "Last 30 min - Realtime".
 */
export const useDeviceDashboardSocket = (assetId, enabled = true) => {
  const [data, setData] = useState(null)
  const [nsyncData, setNsyncData] = useState(null)
  const [connected, setConnected] = useState(false)
  const socketRef = useRef(null)

  useEffect(() => {
    if (!enabled || !assetId || !config.socketUrl) {
      setConnected(false)
      setData(null)
      setNsyncData(null)
      return
    }

    // null when there's no auth token — stay disconnected rather than
    // connecting unauthenticated.
    const socket = connectDeviceDashboardSocket(assetId)
    if (!socket) return
    socketRef.current = socket

    socket.on('connect', () => setConnected(true))
    socket.on('disconnect', () => setConnected(false))
    // Throttled per event stream — payloads are full snapshots (not deltas),
    // so dropping intermediate ones only affects staleness, and re-rendering
    // subscribers on every message is what was burning memory/CPU.
    let lastDeviceAt = 0
    let lastNsyncAt = 0
    socket.on(DEVICE_UPDATE_EVENT, (payload) => {
      const now = Date.now()
      if (now - lastDeviceAt < UPDATE_THROTTLE_MS) return
      lastDeviceAt = now
      setData(payload)
    })
    socket.on(NSYNC_UPDATE_EVENT, (payload) => {
      const now = Date.now()
      if (now - lastNsyncAt < UPDATE_THROTTLE_MS) return
      lastNsyncAt = now
      setNsyncData(payload)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [assetId, enabled])

  return { data, nsyncData, connected }
}

const formatUptime = (milliseconds) => {
  if (milliseconds === undefined || milliseconds === null) return undefined
  // upTime arrives in milliseconds; show it as whole seconds.
  return `${Math.floor(milliseconds / 1000)}s`
}

/**
 * Maps a raw `deviceUpdate` payload (see real sample below) into the shape
 * VIMData.jsx expects. Fields with no known source in the payload are left
 * out entirely so VIMData's own fallback defaults apply.
 *
 * Real payload shape observed:
 * { deviceId, rows: [{ ts, deviceInfo: { iotVolt, chipTemp, ramUsed, upTime,
 *   simNo, ... }, networkData: { nwStrength, nwOperName, ... },
 *   systemData: { connectedSrvr, ... }, ... }] }
 */
export const mapDeviceUpdateToVimData = (deviceUpdate) => {
  const row = deviceUpdate?.rows?.[0]
  if (!row) return undefined

  const { deviceInfo = {}, networkData = {}, systemData = {} } = row

  return {
    voltage: deviceInfo.iotVolt !== undefined ? `${(deviceInfo.iotVolt / 1000).toFixed(1)} V` : undefined,
    temperature: deviceInfo.chipTemp !== undefined ? `${deviceInfo.chipTemp} °C` : undefined,
    connectivity: {
      mqttStatus: systemData.connectedSrvr?.length > 0 ? 'Connected' : 'Disconnected',
      networkStrength: networkData.nwStrength?.[0] !== undefined ? `${networkData.nwStrength[0]} dBm` : undefined,
      networkType: networkData.nwOperName,
      ramUsed: deviceInfo.ramUsed !== undefined ? `${deviceInfo.ramUsed} kB` : undefined,
      uptime: formatUptime(deviceInfo.upTime),
    },
    metadata: {
      simNo: deviceInfo.simNo,
    },
  }
}

/**
 * Maps the unfiltered `nsyncUpdate` payload (full list of active BMS nodes)
 * into the shape ActiveNode.jsx expects. The list length is whatever the
 * backend reports right now (activeCount/nodes.length) — never hardcoded.
 *
 * Real payload shape observed:
 * { deviceId, activeCount, nodes: [{ deviceId, ecuid, sourceAddress,
 *   measurements: { soc_pct, soh_pct, current_A, cells: [mV, ...], ... } }] }
 */
export const mapNsyncUpdateToActiveNodes = (nsyncUpdate) => {
  const nodes = nsyncUpdate?.nodes
  if (!Array.isArray(nodes)) return undefined

  return nodes.map((node) => {
    const m = node.measurements || {}
    const packVoltage = Array.isArray(m.cells) ? m.cells.reduce((sum, mv) => sum + mv, 0) / 1000 : undefined

    return {
      id: node.ecuid,
      ecuid: node.ecuid,
      sourceAddress: node.sourceAddress,
      soc: m.soc_pct,
      soh: m.soh_pct,
      packVoltage: packVoltage !== undefined ? `${packVoltage.toFixed(1)}V` : undefined,
      packCurrent: m.current_A !== undefined ? `${m.current_A > 0 ? '+' : ''}${m.current_A}A` : undefined,
    }
  })
}
